import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "../App.css";
import "./PublicSite.css";
import {
  LicenseIcon,
  EmailIcon,
  PhoneIcon,
  CompanyIcon,
  VerificationIcon,
  SearchIcon,
  CheckIcon,
  LeadsIcon,
} from "../assets/icons/icons";
import clearBadgeMarkWhite from "../assets/clearbadge-icon-white.png";
import TurnstileWidget from "../components/TurnstileWidget";
import LandingFooter from "../components/LandingFooter";
import { trackGoal } from "../utils/datafast";
import { sortLicensesByState, sortStateCodes } from "../utils/licenses";
import { HOME_FAQ_ITEMS } from "../utils/seo";
import useFocusTrap from "../hooks/useFocusTrap";

const modeConfig = {
  phone: { label: "Phone number", placeholder: "e.g. (123) 456-7890" },
  email: { label: "Email address", placeholder: "e.g. jane@company.com" },
  nameState: { label: "First name", placeholder: "e.g. Jane" },
};

const SEARCH_DISCLAIMER_COPY =
  "Search results are based on selected criteria and available profile information. ClearBadge does not rank or recommend professionals based on quality or suitability.";

const RESULTS_PAID_FEATURES_DISCLOSURE =
  "Some professionals provide additional profile information or features through paid ClearBadge services. Paid features do not affect verification status or imply endorsement.";

const VERIFICATION_TOOLTIP_COPY =
  "Verification confirms identity and license status through ClearBadge. It does not imply endorsement, quality, or suitability.";
const UNVERIFIED_TOOLTIP_COPY =
  "Not Verified means this professional has not completed ClearBadge's verification process. It does not imply lack of licensure, quality, or suitability.";
const RESULTS_GROUPING_DISCLOSURE = "Results may be grouped by verification status.";
const PLACEHOLDER_REQUIRED_COPY =
  "We couldn't find a verified profile for this search. This is an unclaimed profile created from search information and/or public licensing data.";
const PAGE_SIZES = [10, 20, 50, 100];
const HERO_VIDEO_ID = "5XzouxyzhJw";
const HOW_TO_GET_STARTED_VIDEO_ID = "gYE6Yyh74hQ";
const LOGO_WHITE_PUBLIC_PATH = "/clearbadge-logo-white.png";
const TURNSTILE_GATE_TIMEOUT_MS = 15000;

const getYoutubeEmbedUrl = (videoId, autoplay = false, prefer1080 = false) =>
  `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1${autoplay ? "&autoplay=1" : ""}${prefer1080 ? "&vq=hd1080&hd=1" : ""}`;

const getYoutubeLoopBackgroundUrl = (videoId, prefer1080 = false) =>
  `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${videoId}&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3&disablekb=1${prefer1080 ? "&vq=hd1080&hd=1" : ""}`;

const getYoutubeThumbUrl = (videoId, high = true) =>
  `https://img.youtube.com/vi/${videoId}/${high ? "maxresdefault" : "hqdefault"}.jpg`;

function VideoSpotlight({
  videoId,
  title,
  caption,
  isPlaying,
  onPlay,
  useLoopPreview = false,
  prefer1080 = false,
  className = "",
}) {
  return (
    <div className={`cb-video-spotlight ${className}`}>
      {isPlaying ? (
        <div className="cb-video-frame-wrap">
          <iframe
            src={getYoutubeEmbedUrl(videoId, true, prefer1080)}
            title={title}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      ) : useLoopPreview ? (
        <div className="cb-video-loop-preview">
          <div className="cb-video-loop-frame" aria-hidden="true">
            <iframe
              src={getYoutubeLoopBackgroundUrl(videoId, prefer1080)}
              title={`${title} background preview`}
              loading="lazy"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              tabIndex={-1}
            />
          </div>
          <span className="cb-video-overlay" aria-hidden="true" />
          <button
            type="button"
            className="cb-video-loop-hit"
            aria-label={`Play ${title}`}
            onClick={onPlay}
          >
            <span className="cb-video-play-btn" aria-hidden="true">
              <span className="cb-video-play-triangle" />
            </span>
          </button>
          {caption ? <span className="cb-video-caption">{caption}</span> : null}
        </div>
      ) : (
        <button
          type="button"
          className="cb-video-preview"
          aria-label={`Play ${title}`}
          onClick={onPlay}
        >
          <img
            src={getYoutubeThumbUrl(videoId, true)}
            alt={`${title} preview`}
            loading="lazy"
            onError={(event) => {
              const img = event.currentTarget;
              if (img.dataset.fallbackApplied === "true") return;
              img.dataset.fallbackApplied = "true";
              img.src = getYoutubeThumbUrl(videoId, false);
            }}
          />
          <span className="cb-video-overlay" aria-hidden="true" />
          <span className="cb-video-play-btn" aria-hidden="true">
            <span className="cb-video-play-triangle" />
          </span>
          {caption ? <span className="cb-video-caption">{caption}</span> : null}
        </button>
      )}
    </div>
  );
}

function HeroVideoOverlay({ videoId, title, onOpen, prefer1080 = false }) {
  return (
    <div className="cb-hero-video-showcase">
      <div className="cb-hero-video-shell">
        <div className="cb-hero-video-frame">
          <iframe
            src={getYoutubeLoopBackgroundUrl(videoId, prefer1080)}
            title={`${title} background preview`}
            loading="lazy"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            tabIndex={-1}
          />
          <div className="cb-hero-video-overlay" aria-hidden="true" />
          <button
            type="button"
            className="cb-hero-video-play"
            aria-label={`Play ${title}`}
            onClick={onOpen}
          >
            <span className="cb-hero-video-play-triangle" />
          </button>
          <button
            type="button"
            className="cb-hero-video-chip"
            aria-label={`Open ${title}`}
            onClick={onOpen}
          >
            Watch Demo
          </button>
        </div>
      </div>
    </div>
  );
}

const getLicenseKey = (license) =>
  String(
    license?.license_id ||
    license?.licenseId ||
    license?.id ||
    license?.license_number ||
    license?.licenseNumber ||
    `${license?.state || ""}-${license?.industry || ""}`
  );

const getDirectLicenseUrl = (license) => {
  if (!license) return "";
  const candidates = [license.doc_url, license.docUrl, license.supporting_document, license.supportingDocument];
  for (const candidate of candidates) {
    const value = String(candidate || "").trim();
    if (value.startsWith("http://") || value.startsWith("https://")) {
      return value;
    }
  }
  return "";
};

const hasLinkedDocument = (license) => {
  if (!license) return false;
  if (license.has_document === false || license.hasDocument === false) return false;
  if (license.has_document === true || license.hasDocument === true) return true;
  if (getDirectLicenseUrl(license)) return true;
  const storagePath = String(license.supporting_document || license.supportingDocument || "").trim();
  return storagePath.length > 0;
};

const buildPaginationItems = (current, total) => {
  if (total <= 1) return [];
  const items = [];
  const pushRange = (start, end) => {
    for (let i = start; i <= end; i += 1) {
      items.push(i);
    }
  };

  if (total <= 7) {
    pushRange(1, total);
    return items;
  }

  items.push(1);
  if (current <= 4) {
    pushRange(2, 5);
    items.push("dots");
    items.push(total);
    return items;
  }

  if (current >= total - 3) {
    items.push("dots");
    pushRange(Math.max(2, total - 4), total - 1);
    items.push(total);
    return items;
  }

  items.push("dots");
  pushRange(current - 1, current + 1);
  items.push("dots");
  items.push(total);
  return items;
};

const resolveTurnstileSiteKey = () => {
  const key = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
  if (typeof window === "undefined") return key;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "";
  return key;
};

const getTurnstileErrorMessage = (code) => {
  const normalized = String(code || "").trim();
  if (!normalized) return "Security check failed. Please try your search again.";
  if (normalized === "timeout" || normalized === "Turnstile load timeout") {
    return "Security check timed out. Please try your search again.";
  }
  return `Security check failed (${normalized}). Please try your search again.`;
};

export default function PublicSearch({ embedded = false }) {
  const location = useLocation();
  const isVerifyPage = location.pathname === "/";
  const isFindProPage =
    location.pathname.startsWith("/find-a-pro") || location.pathname.startsWith("/find-pro");
  const isPricingPage = location.pathname.startsWith("/pricing");
  const isAboutPage = location.pathname.startsWith("/about");
  const isPrivacyPage = location.pathname.startsWith("/privacy-policy");
  const isTermsPage = location.pathname.startsWith("/terms-of-service");
  const TURNSTILE_SITE_KEY = resolveTurnstileSiteKey();
  const [mode, setMode] = useState("phone");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openTooltipFor, setOpenTooltipFor] = useState(null);
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [pendingPayload, setPendingPayload] = useState(null);
  const [gateState, setGateState] = useState(null); // null | "captcha" | "fetching"
  const [formValues, setFormValues] = useState({
    phone: "",
    email: "",
    firstName: "",
    lastName: "",
    state: "FL",
  });
  const [groupedResults, setGroupedResults] = useState([]);
  const [resultType, setResultType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteModalUrl, setInviteModalUrl] = useState("");
  const [inviteCopied, setInviteCopied] = useState(false);
  const [autoOpenSlug, setAutoOpenSlug] = useState("");
  const [heroVideoModalOpen, setHeroVideoModalOpen] = useState(false);
  const [demoVideoPlaying, setDemoVideoPlaying] = useState(false);
  const [showFloatingNav, setShowFloatingNav] = useState(true);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const inviteCopyTimerRef = useRef(null);
  const lastScrollYRef = useRef(0);
  const searchIntentRef = useRef(false);
  const mobileDrawerRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);
  const heroVideoModalRef = useRef(null);
  const heroVideoCloseRef = useRef(null);
  const profileModalRef = useRef(null);
  const profileCloseButtonRef = useRef(null);
  const inviteModalRef = useRef(null);
  const inviteCloseButtonRef = useRef(null);
  const [licenseDocStatus, setLicenseDocStatus] = useState({});
  const [licenseDocUrls, setLicenseDocUrls] = useState({});
  const landingRootRef = useRef(null);
  const handleTurnstileError = useCallback((code) => {
    setCaptchaOpen(false);
    setPendingPayload(null);
    setGateState(null);
    setTurnstileToken("");
    setError(getTurnstileErrorMessage(code));
    if (searchIntentRef.current) {
      searchIntentRef.current = false;
    }
  }, []);
  const showResultsPanel = gateState !== null || loading || !!error || groupedResults.length > 0 || resultType !== null;
  const totalPages = Math.max(1, Math.ceil(groupedResults.length / pageSize));
  const paginationItems = useMemo(
    () => buildPaginationItems(currentPage, totalPages),
    [currentPage, totalPages]
  );
  const paginatedResults = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return groupedResults.slice(start, start + pageSize);
  }, [currentPage, groupedResults, pageSize]);

  const statCards = [
    {
      value: "2.5M+",
      title: "Verifications Searched",
      description: "Trusted by consumers nationwide",
      icon: <LeadsIcon className="stat-icon" />,
    },
    {
      value: "50+",
      title: "State Coverage",
      description: "Comprehensive coverage across the US",
      icon: <LicenseIcon className="stat-icon" />,
    },
    {
      value: "99.9%",
      title: "Satisfaction Score",
      description: "The verification standard for licensed professionals",
      icon: <CheckIcon className="stat-icon fill" />,
    },
    {
      value: "24/7",
      title: "Instant access",
      description: "Verify credentials anytime, anywhere",
      icon: <PhoneIcon className="stat-icon" />,
    },
  ];

  const partnerLogos = [
    "Real Estate",
    "Insurance",
    "Construction",
    "Legal",
    "Education",
    "Finance",
    "Engineering",
  ];

  const coreFeatureGroups = [
    {
      id: "consumer-tools",
      title: "Consumer Tools",
      description: "Give consumers a direct path to confidence before they book or buy.",
      videoId: HERO_VIDEO_ID,
      videoTitle: "Consumer tools demo",
      items: ["License Verification", "Find a Pro"],
    },
    {
      id: "trust-signaling",
      title: "Trust Signaling",
      description: "Turn verification into visible proof across customer touchpoints.",
      videoId: "iKhYwKOclU0",
      videoTitle: "Trust signaling demo",
      items: [
        "Live Verification Badge",
        "Verified Digital Business Card",
        "Verified Custom Email Signature",
      ],
    },
    {
      id: "professional-tools",
      title: "Licensed Professional Tools",
      description: "Everything needed to grow, convert, and stay compliant in one profile system.",
      videoId: "hgqKMMgHxb8",
      videoTitle: "Licensed professional tools demo",
      items: [
        "Trust Sessions",
        "Lead Generation Funnel",
        "Secure Link Generator",
        "License Renewal Notices",
        "Premium Category Visibility",
        "Profile Analytics",
      ],
    },
  ];

  const howCards = [
    {
      title: "Enter Information",
      description:
        "Search by phone number, email address, or name and state to find a licensed professional.",
      points: [
        "Search by phone, email, or name + state",
        "Verification status shown clearly",
        "Consistent profile detail structure",
      ],
      icon: <SearchIcon className="step-icon step-icon-fill" />,
    },
    {
      title: "Instant Verification",
      description: "We check our official state licensed verified database.",
      points: [
        "Digital card + custom signature",
        "Secure links for sharing",
        "Profile visibility controls",
      ],
      icon: <VerificationIcon className="step-icon" />,
    },
    {
      title: "View Results",
      description: "See license status, expiration date, and more details instantly.",
      points: [
        "Lead generation workflows",
        "Renewal notice support",
        "Profile analytics and optimization",
      ],
      icon: <LicenseIcon className="step-icon" />,
    },
  ];

  const faqItems = HOME_FAQ_ITEMS;

  const initialsFrom = (first, last) => {
    const f = first?.[0] || "";
    const l = last?.[0] || "";
    return (f + l).toUpperCase() || "U";
  };

  const isTempPlaceholderId = (value) => String(value || "").startsWith("temp-");
  const getInvitePathForId = (id) =>
    id && !isTempPlaceholderId(id) ? `/invite/${id}` : "/invite";

  useEffect(() => {
    return () => {
      if (inviteCopyTimerRef.current) clearTimeout(inviteCopyTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const rootNode = landingRootRef.current;
    if (!rootNode) return undefined;
    const revealNodes = rootNode.querySelectorAll("[data-reveal]");
    if (!revealNodes.length) return undefined;

    if (embedded) {
      revealNodes.forEach((node) => node.classList.add("is-visible"));
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      revealNodes.forEach((node) => node.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.16, rootMargin: "0px 0px -10% 0px" }
    );

    revealNodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [embedded, location.pathname]);

  useEffect(() => {
    if (embedded) return undefined;
    lastScrollYRef.current = window.scrollY || 0;

    const onScroll = () => {
      const currentY = window.scrollY || 0;
      const previousY = lastScrollYRef.current;
      const delta = currentY - previousY;
      if (Math.abs(delta) < 6) return;

      if (currentY <= 24) {
        setShowFloatingNav(true);
      } else if (delta > 0) {
        setShowFloatingNav(false);
      } else {
        setShowFloatingNav(true);
      }

      lastScrollYRef.current = currentY;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [embedded]);

  useEffect(() => {
    if (mobileMenuOpen) setShowFloatingNav(true);
  }, [mobileMenuOpen]);

  useEffect(() => {
    setCurrentPage(1);
  }, [groupedResults]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  useEffect(() => {
    const handleDocClick = (event) => {
      const target = event?.target;
      if (!target?.closest?.(".info-tooltip-wrap")) {
        setOpenTooltipFor(null);
      }
    };
    const handleKeyDown = (event) => {
      if (event?.key === "Escape") setOpenTooltipFor(null);
    };

    document.addEventListener("click", handleDocClick);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("click", handleDocClick);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const renderVerificationPill = (isVerified, tooltipKey) => {
    const pillKey = `pill-${tooltipKey}`;
    return (
      <span className="info-tooltip-wrap pill-tooltip-wrap">
        <button
          type="button"
          className={`pill-tooltip-btn ${isVerified ? "license-verify-badge vettify-pill-badge" : "license-unverified-badge"}`}
          aria-label={isVerified ? "Verified" : "Not Verified"}
          aria-expanded={openTooltipFor === pillKey}
          onClick={(e) => {
            e.stopPropagation();
            setOpenTooltipFor((prev) => (prev === pillKey ? null : pillKey));
          }}
        >
          <span className="vettify-pill__icon small-dot">
            <img src={clearBadgeMarkWhite} alt="" />
          </span>
          <span className="vettify-pill__text">{isVerified ? "Verified" : "Not Verified"}</span>
        </button>
        <span role="tooltip" className={`info-tooltip ${openTooltipFor === pillKey ? "open" : ""}`}>
          {isVerified ? VERIFICATION_TOOLTIP_COPY : UNVERIFIED_TOOLTIP_COPY}
        </span>
      </span>
    );
  };

  const renderStaticVerificationBadge = (isVerified) => (
    <span className={isVerified ? "license-verify-badge vettify-pill-badge" : "license-unverified-badge"}>
      <span className="vettify-pill__icon small-dot">
        <img src={clearBadgeMarkWhite} alt="" />
      </span>
      <span className="vettify-pill__text">{isVerified ? "Verified" : "Not Verified"}</span>
    </span>
  );

  const groupLicenses = (items = []) => {
    const map = new Map();
    items.forEach((item, index) => {
      const emailDisplay = item.email_display || item.emailDisplay || "";
      const phoneDisplay = item.phone_display || item.phoneDisplay || "";
      const emailRaw = item.email || "";
      const phoneRaw = item.phone || "";
      const identifier = [item.public_slug || item.publicSlug || "", item.license_id || item.licenseId || "", item.first_name || "", item.last_name || ""]
        .join("|")
        .toLowerCase();
      const key = item.public_slug || item.publicSlug || item.license_id || item.licenseId || identifier || `person-${index}`;
      if (!map.has(key)) {
        map.set(key, {
          id: key,
          firstName: item.first_name || "",
          lastName: item.last_name || "",
          email: emailRaw,
          phone: phoneRaw,
          emailDisplay,
          phoneDisplay,
          company: item.company_agency || "",
          licenses: [],
          industries: new Set(),
          states: new Set(),
          profilePhoto: item.profile_photo || null,
          publicSlug: item.public_slug || "",
          hasVerified: false,
          hasUnverified: false,
          unverifiedLicenseId: null,
          createdFromSearch: false,
        });
      }
      const group = map.get(key);
      group.licenses.push(item);
      if (item.industry) group.industries.add(item.industry);
      if (item.state) group.states.add(item.state);
      if (item.profile_photo && !group.profilePhoto) group.profilePhoto = item.profile_photo;
      if (!group.email && emailRaw) group.email = emailRaw;
      if (!group.phone && phoneRaw) group.phone = phoneRaw;
      if (!group.emailDisplay && emailDisplay) group.emailDisplay = emailDisplay;
      if (!group.phoneDisplay && phoneDisplay) group.phoneDisplay = phoneDisplay;
      if (!group.publicSlug && (item.public_slug || item.publicSlug)) group.publicSlug = item.public_slug || item.publicSlug || "";
      if (!group.company && item.company_agency) group.company = item.company_agency;
      if (item.verified) group.hasVerified = true;
      if (item.is_unverified) group.hasUnverified = true;
      if (!group.unverifiedLicenseId && item.is_unverified && item.license_id) {
        group.unverifiedLicenseId = item.license_id;
      }
      if (item.created_from_search || item.is_placeholder) {
        group.createdFromSearch = true;
      }
    });
    const groups = Array.from(map.values()).map((group) => ({
      ...group,
      licenses: sortLicensesByState(group.licenses),
      industries: Array.from(group.industries),
      states: sortStateCodes(Array.from(group.states)),
    }));
    groups.sort((a, b) => {
      const aKey = a.hasVerified ? 0 : 1;
      const bKey = b.hasVerified ? 0 : 1;
      if (aKey !== bKey) return aKey - bKey;
      const lastCmp = String(a.lastName || "").localeCompare(String(b.lastName || ""), undefined, { sensitivity: "base" });
      if (lastCmp !== 0) return lastCmp;
      return String(a.firstName || "").localeCompare(String(b.firstName || ""), undefined, { sensitivity: "base" });
    });
    return groups;
  };

  const performSearch = async (payload) => {
    setError("");
    setGroupedResults([]);
    setResultType(null);
    setLoading(true);
    let didSucceed = false;
    try {
      const token = String(turnstileToken || "").trim();
      const res = await fetch(`${process.env.REACT_APP_API_URL || ""}/api/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { "x-turnstile-token": token } : {}) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        let message = text;
        try {
          const parsed = JSON.parse(text);
          message = parsed?.error || text;
        } catch (_err) {
          // ignore
        }
        if (res.status === 400 || res.status === 403) {
          if (String(message || "").toLowerCase().includes("captcha")) {
            setTurnstileToken("");
            setCaptchaOpen(true);
            setGateState("captcha");
            setPendingPayload(payload);
            setError("");
            return;
          }
        }
        throw new Error(message || "Search failed");
      }
      const data = await res.json();
      setResultType(data.resultType || null);
      const list = data.results || [];
      setGroupedResults(groupLicenses(list));
      didSucceed = true;
      if (searchIntentRef.current) {
        const rawMode = String(payload?.mode || "").trim();
        const searchMode = rawMode === "nameState" ? "name_state" : rawMode || "unknown";
        const params = {
          context: embedded ? "internal" : "public",
          search_mode: searchMode,
        };
        const state = String(payload?.state || "").trim().toLowerCase();
        if (state) params.state = state;
        trackGoal("licenses_searched", params);
        searchIntentRef.current = false;
      }
      setGateState(null);
    } catch (err) {
      setError(err.message || "Unable to search right now.");
      setGroupedResults([]);
      setResultType(null);
      if (!String(err?.message || "").toLowerCase().includes("captcha")) {
        setGateState(null);
      }
    } finally {
      if (!didSucceed && searchIntentRef.current) {
        searchIntentRef.current = false;
      }
      setTurnstileToken("");
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    searchIntentRef.current = true;
    const phoneValue = mode === "phone" ? sanitizePhone(formValues.phone) : formValues.phone;
    const payload = {
      mode,
      phone: phoneValue.trim(),
      email: formValues.email.trim(),
      firstName: formValues.firstName.trim(),
      lastName: formValues.lastName.trim(),
      state: formValues.state,
    };
    if (TURNSTILE_SITE_KEY && !String(turnstileToken || "").trim()) {
      setPendingPayload(payload);
      setCaptchaOpen(true);
      setGateState("captcha");
      return;
    }
    if (TURNSTILE_SITE_KEY) setGateState("fetching");
    performSearch(payload);
  };

  useEffect(() => {
    if (!captchaOpen) return;
    if (!pendingPayload) return;
    if (!String(turnstileToken || "").trim()) return;
    const payload = pendingPayload;
    setPendingPayload(null);
    setCaptchaOpen(false);
    setGateState("fetching");
    performSearch(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captchaOpen, pendingPayload, turnstileToken]);

  useEffect(() => {
    if (gateState !== "captcha") return;
    const timer = setTimeout(() => {
      if (String(turnstileToken || "").trim()) return;
      handleTurnstileError("timeout");
    }, TURNSTILE_GATE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [gateState, turnstileToken, handleTurnstileError]);

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    const parts = [];
    if (digits.length > 0) parts.push("(", digits.slice(0, 3));
    if (digits.length >= 4) parts.push(") ", digits.slice(3, 6));
    if (digits.length >= 7) parts.push("-", digits.slice(6, 10));
    return parts.join("");
  };

  const sanitizePhone = (value) => value.replace(/\D/g, "");

  const resolveLicenseUrl = async (license) => {
    if (!license) return "";
    const direct = getDirectLicenseUrl(license);
    if (direct) return direct;
    if (!hasLinkedDocument(license)) return "";
    const licenseId = license.license_id || license.licenseId || license.id;
    if (!licenseId) return "";
    if (!license.verified) return "";
    try {
      const state = String(license.state || "").trim();
      const query = state ? `?state=${encodeURIComponent(state)}` : "";
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || ""}/api/license-doc/${encodeURIComponent(String(licenseId))}${query}`
      );
      if (!res.ok) return "";
      const data = await res.json();
      return data?.url || "";
    } catch (_err) {
      return "";
    }
  };

  const openProfile = (person) => {
    setOpenTooltipFor(null);
    setLicenseDocStatus({});
    setLicenseDocUrls({});
    setSelectedProfile(person);
  };

  const shouldIgnoreCardClick = (event) => {
    const interactive = event.target?.closest?.("button, a, input, select, textarea, [role='button']");
    return !!interactive && interactive !== event.currentTarget;
  };

  const handleResultCardClick = (event, person) => {
    if (shouldIgnoreCardClick(event)) return;
    openProfile(person);
  };

  const handleResultCardKeyDown = (event, person) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    if (shouldIgnoreCardClick(event)) return;
    event.preventDefault();
    openProfile(person);
  };

  const closeProfile = () => {
    setOpenTooltipFor(null);
    setInviteModalOpen(false);
    setLicenseDocStatus({});
    setLicenseDocUrls({});
    setSelectedProfile(null);
  };

  useEffect(() => {
    if (!selectedProfile) return;
    let cancelled = false;
    const loadDocStatus = () => {
      const licenses = sortLicensesByState(selectedProfile.licenses);
      const statusMap = {};
      const urlMap = {};
      licenses.forEach((lic) => {
        if (!lic?.verified) return;
        const licenseKey = getLicenseKey(lic);
        const direct = getDirectLicenseUrl(lic);
        if (direct) {
          statusMap[licenseKey] = "available";
          urlMap[licenseKey] = direct;
          return;
        }
        if (!hasLinkedDocument(lic)) {
          statusMap[licenseKey] = "missing";
        }
      });
      if (cancelled) return;
      setLicenseDocStatus(statusMap);
      setLicenseDocUrls(urlMap);
    };
    loadDocStatus();
    return () => {
      cancelled = true;
    };
  }, [selectedProfile]);

  const handleLicenseOpen = async (license, licenseKey) => {
    const cachedUrl = licenseDocUrls[licenseKey];
    if (cachedUrl) {
      window.open(cachedUrl, "_blank", "noopener,noreferrer");
      return;
    }
    const link = await resolveLicenseUrl(license);
    if (link) {
      window.open(link, "_blank", "noopener,noreferrer");
      setLicenseDocStatus((prev) => ({ ...prev, [licenseKey]: "available" }));
      setLicenseDocUrls((prev) => ({ ...prev, [licenseKey]: link }));
      return;
    }
    setLicenseDocStatus((prev) => ({ ...prev, [licenseKey]: "missing" }));
  };

  const openInviteModal = (id) => {
    setInviteCopied(false);
    const path = getInvitePathForId(id);
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    setInviteModalUrl(`${origin}${path}`);
    setInviteModalOpen(true);
  };

  const closeInviteModal = () => {
    setInviteModalOpen(false);
    setInviteCopied(false);
  };

  const closeHeroVideoModal = () => {
    setHeroVideoModalOpen(false);
  };

  useFocusTrap({
    isOpen: !embedded && mobileMenuOpen,
    containerRef: mobileDrawerRef,
    onClose: () => setMobileMenuOpen(false),
    returnFocusRef: mobileMenuButtonRef,
    lockScroll: true,
  });

  useFocusTrap({
    isOpen: heroVideoModalOpen,
    containerRef: heroVideoModalRef,
    onClose: closeHeroVideoModal,
    initialFocusRef: heroVideoCloseRef,
    lockScroll: true,
  });

  useFocusTrap({
    isOpen: !!selectedProfile,
    containerRef: profileModalRef,
    onClose: closeProfile,
    initialFocusRef: profileCloseButtonRef,
    lockScroll: true,
  });

  useFocusTrap({
    isOpen: inviteModalOpen,
    containerRef: inviteModalRef,
    onClose: closeInviteModal,
    initialFocusRef: inviteCloseButtonRef,
    lockScroll: true,
  });

  const handleCopyInviteLink = async () => {
    const text = String(inviteModalUrl || "").trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setInviteCopied(true);
    } catch (_err) {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.top = "-1000px";
        textarea.style.left = "-1000px";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        setInviteCopied(true);
      } catch (_err2) {
        return;
      }
    }

    if (inviteCopyTimerRef.current) clearTimeout(inviteCopyTimerRef.current);
    inviteCopyTimerRef.current = setTimeout(() => setInviteCopied(false), 1200);
  };

  // Prefill from URL query params and optionally auto-search
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const phoneParam = params.get("phone");
    const emailParam = params.get("email");
    const first = params.get("firstName");
    const last = params.get("lastName");
    const stateParam = params.get("state");
    const modeParam = params.get("mode");
    const slugParam = params.get("slug");
    const autoOpenParam = params.get("open");

    if (modeParam === "email") setMode("email");
    else if (modeParam === "nameState") setMode("nameState");
    else if (modeParam === "phone") setMode("phone");
    else if (modeParam === "slug") setMode("nameState");

    const nextValues = { ...formValues };
    if (phoneParam) nextValues.phone = formatPhone(phoneParam);
    if (emailParam) nextValues.email = emailParam;
    if (first) nextValues.firstName = first;
    if (last) nextValues.lastName = last;
    if (stateParam) nextValues.state = stateParam;
    setFormValues(nextValues);

    const shouldSearch = phoneParam || emailParam || first || last || slugParam;
    if (shouldSearch) {
      const payload = {
        mode: modeParam || (phoneParam ? "phone" : emailParam ? "email" : "nameState"),
        phone: sanitizePhone(phoneParam || ""),
        email: (emailParam || "").trim(),
        firstName: (first || "").trim(),
        lastName: (last || "").trim(),
        state: stateParam || nextValues.state,
        slug: (slugParam || "").trim(),
      };
      if (TURNSTILE_SITE_KEY && !String(turnstileToken || "").trim()) {
        setPendingPayload(payload);
        setCaptchaOpen(true);
        setGateState("captcha");
      } else {
        if (TURNSTILE_SITE_KEY) setGateState("fetching");
        performSearch(payload);
      }
    }
    if (slugParam && autoOpenParam === "1") {
      setAutoOpenSlug(slugParam.toLowerCase());
    } else {
      setAutoOpenSlug("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (!autoOpenSlug || selectedProfile || groupedResults.length === 0) return;
    const match = groupedResults.find(
      (group) => String(group.publicSlug || "").toLowerCase() === autoOpenSlug
    );
    if (match) {
      openProfile(match);
    }
  }, [autoOpenSlug, groupedResults, selectedProfile]);

  useEffect(() => {
    const loadPhoto = async () => {
      if (!selectedProfile) return;
      const slug = selectedProfile.publicSlug;
      if (!slug) return;
      const current = String(selectedProfile.profilePhoto || "").trim();
      if (current && current.startsWith("http")) return;
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL || ""}/api/profile-photo/${encodeURIComponent(slug)}`
        );
        if (!res.ok) return;
        const data = await res.json();
        const url = String(data?.url || "").trim();
        if (!url) return;
        setSelectedProfile((prev) => {
          if (!prev) return prev;
          if (String(prev.profilePhoto || "").trim() === url) return prev;
          return { ...prev, profilePhoto: url };
        });
      } catch (_err) {
        // ignore
      }
    };
    loadPhoto();
  }, [selectedProfile]);

  const renderFields = () => {
    if (mode === "phone") {
      return (
        <div className="form-row">
          <label htmlFor="public-search-phone">Phone number</label>
          <input
            id="public-search-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            aria-label="Phone number"
            placeholder={modeConfig.phone.placeholder}
            value={formValues.phone}
            onChange={(e) => setFormValues({ ...formValues, phone: formatPhone(e.target.value) })}
          />
        </div>
      );
    }
    if (mode === "email") {
      return (
        <div className="form-row">
          <label htmlFor="public-search-email">Email address</label>
          <input
            id="public-search-email"
            type="email"
            autoComplete="email"
            aria-label="Email address"
            placeholder={modeConfig.email.placeholder}
            value={formValues.email}
            onChange={(e) => setFormValues({ ...formValues, email: e.target.value })}
          />
        </div>
      );
    }
    return (
      <>
        <div className="form-row dual">
          <div>
            <label htmlFor="public-search-first-name">First name</label>
            <input
              id="public-search-first-name"
              type="text"
              autoComplete="given-name"
              aria-label="First name"
              placeholder={modeConfig.nameState.placeholder}
              value={formValues.firstName}
              onChange={(e) => setFormValues({ ...formValues, firstName: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="public-search-last-name">Last name</label>
            <input
              id="public-search-last-name"
              type="text"
              autoComplete="family-name"
              aria-label="Last name"
              placeholder="e.g. Doe"
              value={formValues.lastName}
              onChange={(e) => setFormValues({ ...formValues, lastName: e.target.value })}
            />
          </div>
        </div>
        <div className="form-row">
          <label htmlFor="public-search-state">State</label>
          <select
            id="public-search-state"
            autoComplete="address-level1"
            aria-label="State"
            value={formValues.state}
            onChange={(e) => setFormValues({ ...formValues, state: e.target.value })}
          >
            {["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"].map((st) => (
              <option key={st} value={st}>{st}</option>
            ))}
          </select>
        </div>
      </>
    );
  };

  return (
    <div
      ref={landingRootRef}
      className={`public-shell ${embedded ? "embedded landing clearbadge-public" : "landing clearbadge-public"}`}
    >
      {!embedded && (
        <>
          <header
            className={`public-topbar landing-topbar ${showFloatingNav ? "" : "landing-topbar--hidden"}`}
          >
            <div className="landing-topbar-inner">
              <div className="landing-topbar-left">
                <Link to="/" className="brand-inline" aria-label="ClearBadge home">
                  <img src={LOGO_WHITE_PUBLIC_PATH} alt="ClearBadge logo" className="brand-logo" />
                </Link>
                <nav className="public-search-nav" aria-label="Search navigation">
                  <Link
                    className={`top-link ${location.pathname === "/" ? "active" : ""}`}
                    to="/"
                    data-reveal
                    style={{ "--reveal-delay": "560ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
                  >
                    Verify a License
                  </Link>
                  <Link
                    className={`top-link ${location.pathname.startsWith("/find-a-pro") ? "active" : ""}`}
                    to="/find-a-pro"
                    data-reveal
                    style={{ "--reveal-delay": "700ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
                  >
                    Find a Pro
                  </Link>
                  <Link
                    className={`top-link ${location.pathname.startsWith("/pricing") ? "active" : ""}`}
                    to="/pricing"
                    data-reveal
                    style={{ "--reveal-delay": "840ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
                  >
                    Pricing
                  </Link>
                  <Link
                    className={`top-link ${location.pathname.startsWith("/about") ? "active" : ""}`}
                    to="/about"
                    data-reveal
                    style={{ "--reveal-delay": "980ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
                  >
                    About
                  </Link>
                </nav>
              </div>
              <div className="public-links landing-topbar-right">
                <Link
                  className="top-link"
                  to="/login"
                  data-reveal
                  style={{ "--reveal-delay": "1120ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
                >
                  Sign In
                </Link>
                <Link
                  className="primary nav-cta mobile-signup"
                  to="/login?mode=signup"
                  data-reveal
                  style={{ "--reveal-delay": "1260ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
                >
                  Get Verified
                </Link>
                <button
                  ref={mobileMenuButtonRef}
                  className="icon-btn mobile-menu-btn"
                  type="button"
                  aria-label="Toggle menu"
                  aria-expanded={mobileMenuOpen}
                  aria-controls="public-search-mobile-menu"
                  onClick={() => setMobileMenuOpen((open) => !open)}
                >
                  <span className="burger-line" aria-hidden="true" />
                </button>
              </div>
            </div>
          </header>
          <div className={`public-mobile-overlay ${mobileMenuOpen ? "open" : ""}`}>
            <button
              type="button"
              className="public-mobile-overlay-dismiss"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
            />
            <div
              id="public-search-mobile-menu"
              ref={mobileDrawerRef}
              className={`public-mobile-drawer ${mobileMenuOpen ? "open" : ""}`}
              role="dialog"
              aria-modal="true"
              aria-label="Mobile navigation"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="public-mobile-header">
                <Link to="/" className="brand-inline" aria-label="ClearBadge home" onClick={() => setMobileMenuOpen(false)}>
                  <img src={LOGO_WHITE_PUBLIC_PATH} alt="ClearBadge logo" className="brand-logo" />
                </Link>
                <button className="icon-btn mobile-drawer-close" type="button" aria-label="Close menu" onClick={() => setMobileMenuOpen(false)}>
                  <span className="mobile-drawer-close-icon" aria-hidden="true" />
                </button>
              </div>
              <div className="public-mobile-links">
                <Link to="/login" className="public-mobile-link" onClick={() => setMobileMenuOpen(false)}>
                  Sign In
                </Link>
                <Link
                  to="/login?mode=signup"
                  className="public-mobile-link primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Get Verified
                </Link>
                <div className="mobile-menu-section">
                  <span className="mobile-menu-label">TOOLS</span>
                  <Link
                    to="/"
                    className={`public-mobile-link secondary ${isVerifyPage ? "active" : ""}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="mobile-link-text">Verify a License</span>
                  </Link>
                  <Link
                    to="/find-a-pro"
                    className={`public-mobile-link secondary ${isFindProPage ? "active" : ""}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="mobile-link-text">Find a Pro</span>
                  </Link>
                </div>
                <div className="mobile-menu-section">
                  <span className="mobile-menu-label">RESOURCES</span>
                  <Link
                    to="/about"
                    className={`public-mobile-link secondary ${isAboutPage ? "active" : ""}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="mobile-link-text">About</span>
                  </Link>
                  <Link
                    to="/pricing"
                    className={`public-mobile-link secondary ${isPricingPage ? "active" : ""}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="mobile-link-text">Pricing</span>
                  </Link>
                  <Link
                    to="/privacy-policy"
                    className={`public-mobile-link secondary ${isPrivacyPage ? "active" : ""}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="mobile-link-text">Privacy Policy</span>
                  </Link>
                  <Link
                    to="/terms-of-service"
                    className={`public-mobile-link secondary ${isTermsPage ? "active" : ""}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="mobile-link-text">Terms of Service</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <main className="landing-main" id={embedded ? undefined : "main-content"}>
        <section className={`hero landing-hero${embedded ? " embedded-hero" : ""}`}>
          <div className="hero-inner">
            {!embedded && (
              <div className="trust-pill" data-reveal style={{ "--reveal-delay": "1420ms" }}>
                <VerificationIcon className="pill-icon" /> Verify credentials in seconds
              </div>
            )}
            <h1 data-reveal style={{ "--reveal-delay": "1580ms" }}>Instant License Lookup</h1>
            <p className="hero-lead" data-reveal style={{ "--reveal-delay": "1740ms" }}>
              Trust the Professionals Who Are Truly Verified.
            </p>

            <div className="search-card" data-reveal style={{ "--reveal-delay": "1900ms" }}>
              <div className="pill-toggle">
                <button type="button" className={mode === "phone" ? "active" : ""} onClick={() => setMode("phone")}>
                  Phone
                </button>
                <button type="button" className={mode === "email" ? "active" : ""} onClick={() => setMode("email")}>
                  Email
                </button>
                <button type="button" className={mode === "nameState" ? "active" : ""} onClick={() => setMode("nameState")}>
                  Name + State
                </button>
              </div>

              <form className="search-form" onSubmit={handleSubmit}>
                {renderFields()}
                <button type="submit" className="primary search-btn" disabled={loading}>
                  {loading ? "Searching..." : "Search"}
                </button>
              </form>
              {!embedded && (
                <div className="sub-link">
                  Licensee? <Link to="/login?mode=signup">Get Verified</Link>
                </div>
              )}
            </div>

            {!showResultsPanel && (
              <div
                className="search-disclaimer-wrap"
                role="note"
                aria-label="Search disclaimer"
                data-reveal
                style={{ "--reveal-delay": "2060ms" }}
              >
                <p className="unverified-disclaimer">{SEARCH_DISCLAIMER_COPY}</p>
              </div>
            )}

            {!embedded && !showResultsPanel && (
              <div className="hero-video-block hero-video-load-in">
                <HeroVideoOverlay
                  videoId={HERO_VIDEO_ID}
                  title="ClearBadge product walkthrough"
                  onOpen={() => setHeroVideoModalOpen(true)}
                  prefer1080
                />
              </div>
            )}

            {showResultsPanel && (
              <div className="panel results-panel">
                {gateState !== null && (
                  <div className="results-gate">
                    <div className="cb-loading-row">
                      <p className="muted cb-loading-text">
                        {gateState === "captcha" ? "Fetching results" : "Finding matches"}
                        <span className="cb-inline-spinner" aria-hidden="true" />
                      </p>
                    </div>
                    {TURNSTILE_SITE_KEY && gateState === "captcha" && (
                      <div className="turnstile-wrap" aria-label="Security check">
                        <div className="turnstile-card turnstile-card--loading">
                          <div className="turnstile-skeleton turnstile-skeleton--bar" aria-hidden="true" />
                          <TurnstileWidget
                            siteKey={TURNSTILE_SITE_KEY}
                            action="public_search"
                            onToken={(token) => setTurnstileToken(String(token || ""))}
                            onError={handleTurnstileError}
                          />
                        </div>
                      </div>
                    )}
                    {TURNSTILE_SITE_KEY && gateState === "fetching" && (
                      <div className="turnstile-wrap" aria-hidden="true">
                        <div className="turnstile-card turnstile-card--placeholder turnstile-card--loading">
                          <div className="turnstile-skeleton turnstile-skeleton--bar" />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {gateState === null && loading && (
                  <p className="muted">
                    Searching <span className="cb-inline-spinner" aria-hidden="true" />
                  </p>
                )}
                {gateState === null && !loading && error && <p className="error-message">{error}</p>}
                {gateState === null && !loading && !error && (
                  <>
                    <p className="muted" style={{ marginBottom: 8 }}>
                      {groupedResults.length === 0
                        ? "No verified profiles found yet."
                        : `Found ${groupedResults.length} potential match${groupedResults.length > 1 ? "es" : ""}.`}
                    </p>
                    {groupedResults.length > 0 && (
                      <p
                        className="unverified-disclaimer results-grouping-disclaimer"
                        role="note"
                        aria-label="Grouping disclosure"
                      >
                        {RESULTS_GROUPING_DISCLOSURE}
                      </p>
                    )}
                    <div className="person-list">
                      {paginatedResults.map((person) => (
                        <article
                          key={person.id}
                          className={`person-card clickable-result-card${person.hasUnverified && !person.hasVerified ? " unverified-card" : ""}`}
                          tabIndex={0}
                          role="button"
                          aria-label={`Open profile for ${(person.firstName || person.lastName) ? `${person.firstName || ""} ${person.lastName || ""}`.trim() : "this result"}`}
                          onClick={(event) => handleResultCardClick(event, person)}
                          onKeyDown={(event) => handleResultCardKeyDown(event, person)}
                        >
                          <div className="person-header">
                            <div>
                              <h4>
                                {(person.firstName || person.lastName)
                                  ? `${person.firstName || ""} ${person.lastName || ""}`.trim()
                                  : "Name unavailable"}
                              </h4>
                              {person.industries.length > 0 && (
                                <div className="industry-pills">
                                  {person.industries.slice(0, 3).map((industry) => (
                                    <span key={industry} className="industry-pill">{industry}</span>
                                  ))}
                                  {person.industries.length > 3 && (
                                    <span className="industry-pill muted-pill">+{person.industries.length - 3}</span>
                                  )}
                                </div>
                              )}
                            </div>
                            {(person.hasVerified || person.hasUnverified) &&
                              renderVerificationPill(!!person.hasVerified, `result-${person.id}`)}
                          </div>
                          <div className="person-meta">
                            {(person.email || person.emailDisplay) && (
                              <span>
                                <EmailIcon className="card-line-icon" />
                                {person.email || person.emailDisplay}
                              </span>
                            )}
                            {(person.phone || person.phoneDisplay) && (
                              <span>
                                <PhoneIcon className="card-line-icon" />
                                {person.phone || person.phoneDisplay}
                              </span>
                            )}
                            {person.company && (
                              <span>
                                <CompanyIcon className="card-line-icon" />
                                {person.company}
                              </span>
                            )}
                            {person.states.length > 0 && (
                              <span>
                                <LicenseIcon className="card-line-icon" />
                                {person.states.join(", ")}
                              </span>
                            )}
                          </div>
                          {person.hasUnverified && !person.hasVerified && (
                            <p className="unverified-disclaimer">
                              {person.createdFromSearch ? "Not found in ClearBadge yet. " : ""}Unclaimed profile.
                            </p>
                          )}
                          {person.hasUnverified && !person.hasVerified ? (
                            <button type="button" className="view-link view-link-btn" onClick={() => openProfile(person)}>
                              View details ->
                            </button>
                          ) : null}
                          {!person.hasUnverified || person.hasVerified ? (
                            <button type="button" className="view-link view-link-btn" onClick={() => openProfile(person)}>
                              View licenses ->
                            </button>
                          ) : null}
                        </article>
                      ))}
                    </div>
                    {totalPages > 1 && (
                      <div className="pagination-bar">
                        <div className="pagination-pill">
                          <button
                            className="pagination-arrow"
                            type="button"
                            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                            disabled={currentPage <= 1}
                            aria-label="Previous page"
                          >
                            {"<"}
                          </button>
                          {paginationItems.map((item, idx) =>
                            item === "dots" ? (
                              <span key={`dots-${idx}`} className="pagination-dots">...</span>
                            ) : (
                              <button
                                key={item}
                                className={`pagination-page ${item === currentPage ? "active" : ""}`}
                                type="button"
                                onClick={() => setCurrentPage(item)}
                                aria-label={`Page ${item}`}
                              >
                                {item}
                              </button>
                            )
                          )}
                          <button
                            className="pagination-arrow"
                            type="button"
                            onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                            disabled={currentPage >= totalPages}
                            aria-label="Next page"
                          >
                            {">"}
                          </button>
                        </div>
                        <div className="pagination-size">
                          <select
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                            aria-label="Results per page"
                          >
                            {PAGE_SIZES.map((size) => (
                              <option key={size} value={size}>
                                {size} / page
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                    <p className="unverified-disclaimer results-footer-disclaimer" role="note" aria-label="Paid features disclosure">
                      {RESULTS_PAID_FEATURES_DISCLOSURE}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </section>

        {!embedded && (
          <>
            <section className="core-features-section" id="features">
              <div className="landing-container">
                <div className="section-head core-section-head">
                  <span
                    className="core-kicker-pill"
                    data-reveal
                    style={{ "--reveal-delay": "0ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}
                  >
                    Core Features
                  </span>
                  <h2 data-reveal style={{ "--reveal-delay": "140ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}>
                    Everything you need to <span className="core-head-gradient">build and prove trust</span>
                  </h2>
                  <p data-reveal style={{ "--reveal-delay": "280ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}>
                    Consumer verification tools, trust signaling, and licensed professional growth features in one platform.
                  </p>
                </div>
                <div className="core-feature-grid">
                  {coreFeatureGroups.map((group, idx) => (
                    <article
                      key={group.title}
                      className="core-feature-card"
                      data-reveal
                      style={{ "--reveal-delay": `${idx * 90}ms` }}
                    >
                      <div className="core-feature-media">
                        <VideoSpotlight
                          className="core-feature-video"
                          videoId={group.videoId}
                          title={group.videoTitle}
                          isPlaying={false}
                          useLoopPreview
                        />
                      </div>
                      <div className="core-feature-body">
                        <span className="core-feature-step">{idx + 1}</span>
                        <h3>{group.title}</h3>
                        <p className="muted">{group.description}</p>
                        <ul>
                          {group.items.map((item, itemIdx) => (
                            <li key={item} style={{ "--item-delay": `${160 + itemIdx * 75}ms` }}>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </section>

            <section className="stats-section" id="trust">
              <div className="landing-container">
                <div className="section-head" data-reveal>
                  <p className="eyebrow trust-kicker">WHY CHOOSE US</p>
                  <h2>Trusted Nationwide</h2>
                  <p>Proof of Trust, One License at a Time.</p>
                </div>
                <div className="stat-card-grid landing-stats">
                  {statCards.map((card, idx) => (
                    <div
                      key={card.title}
                      className="stat-card stat-card-landing"
                      data-reveal
                      style={{ "--reveal-delay": `${idx * 90}ms` }}
                    >
                      <div className="stat-icon-wrap">{card.icon}</div>
                      <div className="stat-value">{card.value}</div>
                      <div className="stat-title">{card.title}</div>
                      <p className="muted small">{card.description}</p>
                    </div>
                  ))}
                </div>
                <div className="partner-strip" data-reveal style={{ "--reveal-delay": "260ms" }}>
                  <p className="eyebrow partner-eyebrow">We verify licensed professionals in many industries</p>
                  <div className="partner-marquee">
                    <div className="marquee-track">
                      {[...partnerLogos, ...partnerLogos, ...partnerLogos].map((name, idx) => (
                        <div key={`${name}-${idx}`} className="partner-logo">
                          <span>{name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="how-section built-section" id="how-it-works">
              <div className="landing-container">
                <div className="built-head">
                  <p
                    className="eyebrow how-it-works-eyebrow"
                    data-reveal
                    style={{ "--reveal-delay": "0ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}
                  >
                    HOW IT WORKS
                  </p>
                  <h2 data-reveal style={{ "--reveal-delay": "140ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}>
                    How <span className="how-title-gradient">ClearBadge<sup>&trade;</sup></span> Works
                  </h2>
                  <p data-reveal style={{ "--reveal-delay": "280ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}>
                    Verify professional licenses in three simple steps. Fast, accurate, and completely free for
                    consumers.
                  </p>
                </div>
                <div className="built-card-grid">
                  {howCards.map((card, idx) => (
                    <article
                      key={card.title}
                      className="built-card"
                      data-reveal
                      style={{ "--reveal-delay": `${idx * 110}ms` }}
                    >
                      <div className="built-card-meta" aria-hidden="true">
                        <span className="built-step-pill">{idx + 1}</span>
                        <span className="built-card-corner-dot" />
                      </div>
                      <div className="built-card-icon">
                        {card.icon}
                      </div>
                      <h3>{card.title}</h3>
                      <p className="muted">{card.description}</p>
                    </article>
                  ))}
                </div>
              </div>
            </section>

            <section className="getting-started-video-section" id="product-demo">
              <div className="landing-container">
                <div className="getting-started-video-head" data-reveal>
                  <h2>HOW TO GET STARTED</h2>
                  <p>Get setup and list your <strong>first license for free</strong></p>
                </div>
                <div className="getting-started-video-wrap" data-reveal>
                  <VideoSpotlight
                    className="getting-started-video-spotlight"
                    videoId={HOW_TO_GET_STARTED_VIDEO_ID}
                    title="ClearBadge getting started walkthrough"
                    isPlaying={demoVideoPlaying}
                    onPlay={() => setDemoVideoPlaying(true)}
                    useLoopPreview
                    prefer1080
                  />
                </div>
              </div>
            </section>

            <section className="faq-section" id="faq">
              <div className="landing-container">
                <div className="faq-split">
                  <div className="faq-intro">
                    <span
                      className="faq-kicker-pill"
                      data-reveal
                      style={{ "--reveal-delay": "0ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}
                    >
                      FAQs
                    </span>
                    <h2 data-reveal style={{ "--reveal-delay": "140ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}>
                      Questions about ClearBadge?
                    </h2>
                    <p data-reveal style={{ "--reveal-delay": "280ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}>
                      Everything you need to know about verification, trust badges, and getting started.
                    </p>
                  </div>
                  <div className="faq-stack" data-reveal>
                    {faqItems.map((item, idx) => {
                      const panelId = `faq-panel-${idx}`;
                      const isOpen = openFaqIndex === idx;
                      return (
                        <article key={item.question} className={`faq-accordion-item ${isOpen ? "open" : ""}`}>
                          <button
                            type="button"
                            className="faq-accordion-trigger"
                            aria-expanded={isOpen}
                            aria-controls={panelId}
                            onClick={() => setOpenFaqIndex((prev) => (prev === idx ? -1 : idx))}
                          >
                            <span>{item.question}</span>
                            <span className="faq-accordion-chevron" aria-hidden="true" />
                          </button>
                          <div id={panelId} className="faq-accordion-panel" hidden={!isOpen}>
                            <p>{item.answer}</p>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>

            <section className="get-started-section" id="get-started">
              <div className="landing-container">
                <div className="get-started-card" data-reveal>
                  <div className="get-started-copy">
                    <p className="eyebrow">GET STARTED TODAY</p>
                    <h2>Ready to get verified?</h2>
                    <p>
                      Join licensed professionals who use ClearBadge to build trust, stand out, and convert more leads.
                    </p>
                  </div>
                  <div className="get-started-actions">
                    <Link className="primary get-started-primary" to="/login?mode=signup">
                      Get Verified Now
                    </Link>
                    <a className="get-started-secondary" href="mailto:support@clearbadge.org">
                      Contact Us
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="get-started-contact-icon"
                        aria-hidden="true"
                      >
                        <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
                        <rect x="2" y="4" width="20" height="16" rx="2" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </section>

            <LandingFooter />
          </>
        )}
      </main>

      {heroVideoModalOpen && (
        <div className="modal-backdrop hero-video-modal-backdrop" onClick={closeHeroVideoModal}>
          <div
            ref={heroVideoModalRef}
            className="modal hero-video-modal"
            role="dialog"
            aria-modal="true"
            aria-label="ClearBadge demo video"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              ref={heroVideoCloseRef}
              className="icon-btn hero-video-close"
              type="button"
              onClick={closeHeroVideoModal}
              aria-label="Close demo video"
            >
              x
            </button>
            <div className="hero-video-modal-frame">
              <iframe
                src={getYoutubeEmbedUrl(HERO_VIDEO_ID, true, true)}
                title="ClearBadge demo video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      {selectedProfile && (
        <div className="modal-backdrop profile-modal-backdrop" onClick={closeProfile}>
          <div
            ref={profileModalRef}
            className="modal profile-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="public-search-profile-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="profile-card-hero">
              <div className="profile-avatar">
                {selectedProfile.profilePhoto && String(selectedProfile.profilePhoto).startsWith("http") ? (
                  <img
                    src={selectedProfile.profilePhoto}
                    alt={selectedProfile.firstName || "Profile"}
                    onError={() => setSelectedProfile((prev) => (prev ? { ...prev, profilePhoto: null } : prev))}
                  />
                ) : (
                  <span>{initialsFrom(selectedProfile.firstName, selectedProfile.lastName)}</span>
                )}
              </div>
              <button
                ref={profileCloseButtonRef}
                className="icon-btn"
                type="button"
                onClick={closeProfile}
                aria-label="Close profile"
              >
                x
              </button>
            </div>
            <div className="profile-card-body">
              <div className="profile-info-top">
                <h2 id="public-search-profile-title">{`${selectedProfile.firstName || ""} ${selectedProfile.lastName || ""}`.trim() || "License Holder"}</h2>
                {(selectedProfile.hasVerified || selectedProfile.hasUnverified) &&
                  renderVerificationPill(!!selectedProfile.hasVerified, "modal-header")}
              </div>
              {selectedProfile.company && (
                <p className="muted" style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <CompanyIcon className="card-line-icon" />
                  {selectedProfile.company}
                </p>
              )}
              <div className="profile-contact">
                {(selectedProfile.email || selectedProfile.emailDisplay) && (
                  <span>
                    <EmailIcon className="card-line-icon" />
                    {selectedProfile.email || selectedProfile.emailDisplay}
                  </span>
                )}
                {(selectedProfile.phone || selectedProfile.phoneDisplay) && (
                  <span>
                    <PhoneIcon className="card-line-icon" />
                    {selectedProfile.phone || selectedProfile.phoneDisplay}
                  </span>
                )}
                {selectedProfile.states.length > 0 && (
                  <span>
                    <LicenseIcon className="card-line-icon" />
                    States: {selectedProfile.states.join(", ")}
                  </span>
                )}
              </div>
              <div className="card-divider dotted" />
            </div>
            {selectedProfile.hasUnverified && !selectedProfile.hasVerified && (
              <div className="unverified-block">
                <p className="unverified-disclaimer">
                  {PLACEHOLDER_REQUIRED_COPY}
                </p>
                <a href="mailto:support@clearbadge.org" className="disclaimer-link unverified-removal-link">
                  Request correction or removal
                </a>
                <div className="unverified-actions">
                  <button
                    type="button"
                    className="primary claim-cta"
                    onClick={() => openInviteModal(selectedProfile.unverifiedLicenseId)}
                  >
                    Invite them to verify
                  </button>
                  <Link
                    to={
                      selectedProfile.unverifiedLicenseId && !isTempPlaceholderId(selectedProfile.unverifiedLicenseId)
                        ? `/claim/${selectedProfile.unverifiedLicenseId}`
                        : "/login?mode=signup"
                    }
                    className="ghost claim-cta"
                  >
                    Claim this profile
                  </Link>
                </div>
              </div>
            )}
            {!selectedProfile.hasUnverified || selectedProfile.hasVerified ? (
              <div className="profile-license-list">
                {sortLicensesByState(selectedProfile.licenses).map((lic) => {
                  const licenseKey = getLicenseKey(lic);
                  const docStatus = licenseDocStatus[licenseKey];
                  return (
                    <button
                      type="button"
                      key={licenseKey}
                      className="profile-license-card"
                      aria-label={`Open license document for ${lic.license_number || lic.licenseNumber || "license"}`}
                      onClick={async () => {
                        await handleLicenseOpen(lic, licenseKey);
                      }}
                    >
                      <div className="license-top license-top-row">
                        <div className="pill-with-icon">
                          <p className="pill">{lic.industry || "License"}</p>
                        </div>
                        {lic.verified
                          ? renderStaticVerificationBadge(true)
                          : lic.is_unverified
                            ? renderStaticVerificationBadge(false)
                            : null}
                      </div>
                      <h4>
                        License # {lic.license_number || lic.licenseNumber || "Unavailable"}
                      </h4>
                      <p className="muted ellipsis">State: {lic.state || "Unavailable"}</p>
                      <div className="license-meta" style={{ gap: 10 }}>
                        {(lic.email || lic.email_display || lic.emailDisplay) && (
                          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <EmailIcon className="card-line-icon" />
                            {lic.email || lic.email_display || lic.emailDisplay}
                          </span>
                        )}
                        {(lic.phone || lic.phone_display || lic.phoneDisplay) && (
                          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <PhoneIcon className="card-line-icon" />
                            {lic.phone || lic.phone_display || lic.phoneDisplay}
                          </span>
                        )}
                        {lic.company_agency && (
                          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <CompanyIcon className="card-line-icon" />
                            {lic.company_agency}
                          </span>
                        )}
                      </div>
                      {lic.verified && (
                        <p className="muted" style={{ marginTop: 8, fontWeight: 600 }}>
                          {docStatus === "missing" ? "No document on file." : "Click to view license"}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {inviteModalOpen && (
        <div className="modal-backdrop" onClick={closeInviteModal}>
          <div
            ref={inviteModalRef}
            className="modal invite-link-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="public-search-invite-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="public-search-invite-title" style={{ margin: 0 }}>Copy invite link</h3>
            </div>
            <p className="unverified-disclaimer" style={{ marginBottom: 12 }}>
              Share this link so the professional can claim and verify their profile.
            </p>
            <div className="link-row">
              <input type="text" value={inviteModalUrl} readOnly className="invite-link-input" />
              <button
                className={`ghost copy-btn ${inviteCopied ? "copied" : ""}`}
                type="button"
                onClick={handleCopyInviteLink}
              >
                <span className="copy-text" aria-hidden={inviteCopied}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="copy-icon">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75" />
                  </svg>
                </span>
                <span className="copy-check" aria-hidden={!inviteCopied}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="copy-icon">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                  </svg>
                </span>
                <span className="sr-only">Copy invite link</span>
              </button>
            </div>
            <div className="unverified-actions" style={{ marginTop: 12 }}>
              <button ref={inviteCloseButtonRef} type="button" className="ghost claim-cta" onClick={closeInviteModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div >
  );
}

